import { useEffect, useState } from 'react';

import { BASE_MEDIUM_FONT_IN_PX, SCALE_ERROR_DELTA } from './zoom.constants';
import type { UsePinchZoomReturn, UseTextZoomReturn } from './zoom.types';
import {
  createHiddenFullscreenElement,
  createHiddenSingleLetterElement,
  getInitialScale,
  subscribeFallbackZoom,
  subscribeVisualViewportChange,
} from './zoom.utils';

// Detects pinch-zoom on iOS Safari
// Subscribes to resize and returns visualViewport.scale.
// Falls back to document.documentElement.clientWidth / window.innerWidth if visualViewport is unavailable.
export const useIOSPinchZoom = (): UsePinchZoomReturn => {
  const [scale, setScale] = useState(getInitialScale);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.visualViewport) {
      return subscribeVisualViewportChange(setScale);
    }
    return subscribeFallbackZoom(setScale);
  }, []);

  return {
    scale,
    isPinchZoomed: scale > 1,
  };
};

// Detects pinch-zoom on Chrome Android
// Subscribes to hidden div resize and computes window.outerWidth / 100vw.
export const useAndroidPinchZoom = (): UsePinchZoomReturn => {
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const hiddenElement = createHiddenFullscreenElement();

    const update = () => {
      const zoomedScreenWidth = parseFloat(
        getComputedStyle(hiddenElement).width
      );
      setScale(window.outerWidth / zoomedScreenWidth);
    };

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(hiddenElement);

    update();

    return () => {
      resizeObserver.disconnect();
      hiddenElement.remove();
    };
  }, []);

  return {
    scale,
    isPinchZoomed: scale > 1 + SCALE_ERROR_DELTA,
  };
};

// Detects text-zoom on Chrome iOS
// Subscribes to hidden div resize, computes `font-size:medium` and compares the result to base value
export const useTextZoom = (): UseTextZoomReturn => {
  const [fontZoom, setFontZoom] = useState(1);

  useEffect(() => {
    const hiddenElement = createHiddenSingleLetterElement();

    const update = () => {
      const current =
        parseFloat(getComputedStyle(hiddenElement).fontSize) ||
        BASE_MEDIUM_FONT_IN_PX;
      setFontZoom(current / BASE_MEDIUM_FONT_IN_PX);
    };

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(hiddenElement);

    update();

    return () => {
      resizeObserver.disconnect();
      hiddenElement.remove();
    };
  }, []);

  const isTextZoomed = fontZoom > 1;

  return { fontZoom, isTextZoomed };
};
